import type { TargetAndTransition, Transition } from "motion/react";

/**
 * Staggered fade-slide-in choreography for the Hearth's stacked modules.
 *
 * The client coaching home blooms top-down on load: greeting first, then the
 * next-moment hero, north-star, what-needs-you, wins, and quiet links — each
 * entering a beat after the one above it. `order` is the module's vertical
 * position (0 = topmost). Reduced motion collapses to a calm, delay-free fade.
 *
 * Aliases: entrance animation, load stagger, fade-slide-in, bloom.
 */

/** DCM "ease-out-expo" house curve — the same easing used across the Hearth. */
const EASE_OUT_EXPO = [0.19, 1, 0.22, 1] as const;

/** Seconds between two consecutive modules entering. */
const STEP_SECONDS = 0.07;
/** Cap so a long Hearth never feels sluggish to finish blooming. */
const MAX_DELAY_SECONDS = 0.42;
const DURATION_SECONDS = 0.45;

export interface EntranceMotion {
  initial: TargetAndTransition;
  animate: TargetAndTransition;
  transition: Transition;
}

/**
 * Motion props for one Hearth module's entrance. Spread onto its root
 * `motion` element: `<motion.section {...hearthEntrance(order, reduce)} />`.
 */
export function hearthEntrance(order: number, reduce: boolean): EntranceMotion {
  if (reduce) {
    return {
      initial: { opacity: 0 },
      animate: { opacity: 1 },
      transition: { duration: 0.2 },
    };
  }

  const delay = Math.min(Math.max(order, 0) * STEP_SECONDS, MAX_DELAY_SECONDS);
  return {
    initial: { opacity: 0, y: 10 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: DURATION_SECONDS, delay, ease: EASE_OUT_EXPO },
  };
}
